import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import * as actions from '@actions';
import * as actionTypes from '@actionTypes';
import { useRouter } from 'next/router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCleanUpAppointmentStorage, useSessionFormState } from '@hooks/useSessionFormState';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import { useInvestigationRequestPDFUtils } from '@components/Clinicians/InvestigationRequest/InvestigationRequestModel';
import { Button, Divider, List, Modal } from 'ALIASED-antd';
import { ApptOutcomeItem } from 'vl-common/src/schemas/getMetaAppointments';
import useAppointmentDetails from '@hooks/appointments/useAppointmentDetails';
import { useSessionAppointmentId } from '@hooks/useSessionAppointmentId';
import { InvestigationRequest } from 'vl-common/src/schemas/shared/investigationRequest';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import useVoiceCalling from '@hooks/useVoiceCalling';
import useAudio from '@hooks/useAudio';
import ProviderCard from '@components/Clinicians/InvestigationRequest/ProviderCard';
import useMedicationsLoader from '@hooks/useMedicationsLoader';
import useAppointmentPrescriptions from '@hooks/useAppointmentPrescriptions';
import { useDPACheck } from '@hooks/useDPACheck';
import { useValidateActionTab } from '@hooks/useValidateActionTab';
import useCallStatusUpdaters from '@hooks/useCallStatusUpdaters';
import useCallStatus from '@hooks/useCallStatus';
import { Survey } from 'vl-common/src/schemas/shared';
import useAppointmentMeta from '@components/Clinicians/Session/hooks/useAppointmentMeta';
import useProgressValidationModal from './useProgressValidationModal';
import { useValidateClinicalTab } from './useValidateClinicalTab';
import { useValidateDocmanDiscussionFormField } from '@components/Clinicians/Session/Components/DocmanDiscussionFormItem';
import { useTranslation } from 'react-i18next';
import { dataTestIdFromTitle } from 'vl-common/src/utils';
import useAppointmentEvents from '@hooks/useAppointmentEvents';
import { CaseReviewApptTypes } from 'vl-common/src/hooks/useAppointmentManagmentActions';

const AppointmentVersionEnum = {
  V1: 'V1',
  V2: 'V2'
};

const FooterDiv = styled.div`
  display: flex;
  margin-top: 10px;
  justify-content: end;
  gap: 4px;
`;

const ContentDiv = styled.div``;

const Container = styled.div``;

const useCompleteAppointment = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const router = useRouter();
  const appointmentId = useSessionAppointmentId();

  const { appointment, refetchAppointment } = useSessionAppointment();
  const { endCall, updatersReady } = useCallStatusUpdaters(appointment);
  const callStatus = useCallStatus(appointment);
  const { close: closeWS } = useAppointmentEvents(appointment);
  const { detailsCurrentAppointment } = useAppointmentDetails(appointmentId);
  const { validateDPACheck } = useDPACheck();
  const validateClinicalTab = useValidateClinicalTab();
  const validateActionTab = useValidateActionTab();
  const validateDocmanDiscussionFormField = useValidateDocmanDiscussionFormField();
  const cleanUpAppointmentStorage = useCleanUpAppointmentStorage();
  const { loadProgressValidationModal, updateProgressModal } = useProgressValidationModal({
    modalTitle: 'Completing Appointment...',
    initialMessage: 'Initializing Completion Process',
    onSuccessfulCompletion: () => redirectAppointment(),
    onFailedCompletion: () => {},
    onSuccessText: 'Go to Appointments',
    onFailText: 'Close'
  });

  const medicationsLoader = useMedicationsLoader(appointment.clinician_user_guid, appointmentId);
  const { prescribeMedication, canPrescribe } = useAppointmentPrescriptions();

  const [, setAlert] = useSessionFormState('alert', null);
  const [letterViewed] = useSessionFormState('letterViewed', false);
  const [appointmentOutcome] = useSessionFormState('appointment_outcome');
  const [tableMedications, setTableMedications] = useSessionFormState('tableMedications');
  const [letterReq] = useSessionFormState('letterReq');
  const [f2fReferralPosted] = useSessionFormState('f2fReferralPosted', appointment?.referral_created);

  const appointmentType =
    Number(detailsCurrentAppointment?.appt_version) <= 1 || detailsCurrentAppointment?.appt_id === null
      ? AppointmentVersionEnum.V1
      : AppointmentVersionEnum.V2;

  const isAppointmentV1 = appointmentType === AppointmentVersionEnum.V1;
  const isAppointmentV2 = appointmentType === AppointmentVersionEnum.V2;

  const { loadPDFDocument, getMissingFields, flattenAndSave, loadInvestigationRequests } =
    useInvestigationRequestPDFUtils(appointmentId);

  const [, , stopVideoRinger] = useAudio('/static/telephone-ring.mp3', 0.6);

  const { onClickHangupCall } = useVoiceCalling(appointment, stopVideoRinger, refetchAppointment);

  const { data: appointmentMeta } = useAppointmentMeta();

  const { data: assessmentSurveys = [] } = useQuery({
    queryKey: ['getAssessmentSurveys', appointment.appointment_id],
    queryFn: async () => {
      const res = await dispatch(
        actions.getAssessmentSurveys({
          appt_id: String(appointment.appointment_id),
          response: 1
        })
      );

      return res.type === actionTypes.ASSESSMENT_SURVEY_SUCCESS && res.payload ? res.payload : [];
    }
  });

  const isAuditSurveyCompleted = assessmentSurveys.find(
    (survey: Survey) => survey.shortname === 'Audit Survey'
  )?.completed_flag;

  const { mutateAsync: getAppointmentDetails } = useMutation({
    mutationFn: async () => {
      return dispatch(actions.getAppointmentDetails(appointmentId));
    }
  });

  const { mutateAsync: pdfSave } = useMutation({
    mutationFn: async (isPreview: boolean) => {
      return dispatch(
        actions.saveLamdaPDF(appointment.clinician_user_guid, appointmentId, appointment.patient_user_guid, {
          preview: isPreview,
          ...letterReq
        })
      );
    }
  });

  const { mutateAsync: deleteInvestigationRequests, isPending: deleteInvestigationRequestsLoading } = useMutation({
    mutationFn: async (investigationRequests: InvestigationRequest[]) => {
      try {
        const promises = investigationRequests.map(async (investigationRequest: InvestigationRequest) => {
          await dispatch(actions.deleteInvestigationRequest(appointment.appointment_id, investigationRequest.ireq_id));
          return queryClient.invalidateQueries({ queryKey: [['getLetterFiles', appointment.appointment_id]] });
        });
        await Promise.all(promises);
        return Promise.resolve();
      } catch {
        return Promise.reject();
      }
    },
    onSuccess: () => {
      Modal.success({
        title: t('Investigation Requests Removed'),
        content: 'All investigation requests have been removed.',
        modalRender: (originalNode) => <div data-testid="investigation-requests-removed-modal">{originalNode}</div>,

        onOk: () => {
          Modal.destroyAll();
          onCompleteAppointment();
        }
      });
    },
    onError: () => {
      setAlert({
        type: 'error',
        message: 'There was an error removing the investigation requests.'
      });
      Modal.error({
        title: t('Error Removing Investigation Requests'),
        content: 'There was an error removing the investigation requests.',
        modalRender: (originalNode) => (
          <div data-testid="error-removing-investigation-requests-modal">{originalNode}</div>
        ),
        onOk: () => {
          Modal.destroyAll();
          onCompleteAppointment();
        }
      });
    }
  });

  const onCompleteError = useCallback(
    ({
      titleMsg = 'Complete Appointment',
      errorMsg = 'There was an error completing the appointment.'
    }: {
      titleMsg?: string | React.ReactNode;
      errorMsg?: string | React.ReactNode;
    }) => {
      setAlert({
        type: 'error',
        message: errorMsg
      });
      Modal.error({
        title: titleMsg,
        content: errorMsg,
        modalRender: (originalNode) => <div data-testid={dataTestIdFromTitle(titleMsg)}>{originalNode}</div>
      });
    },
    [setAlert]
  );

  const redirectAppointment = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['clinicianAppointment']
    });
    router.push('/clinician/appointments');
  }, [router]);

  const getIncompleteMedication = useCallback(
    () => tableMedications?.find(({ duration, frequency }) => !duration?.trim() || !frequency?.trim()),
    [tableMedications]
  );

  const prepareDocumentsOnCompleteAppointment = useCallback(async () => {
    const imgDetailsRes = await loadInvestigationRequests(() => {});
    for (const { ireq_id, form_type } of imgDetailsRes) {
      if (form_type !== 'S') {
        const { error, document } = await loadPDFDocument(ireq_id);
        if (error || !document) return { failedToSend: true };
        if (!(await flattenAndSave(document, ireq_id))) {
          return { failedToSend: true };
        }
      }
    }

    return { failedToSend: false };
  }, [flattenAndSave, loadInvestigationRequests, loadPDFDocument]);

  const completeAppointment = useCallback(async () => {
    const ProgressModal = loadProgressValidationModal();
    const investigationRequests = await loadInvestigationRequests(() => {});

    const option = {
      user_guid: appointment.clinician_user_guid,
      invreqs: investigationRequests,
      appt_id: appointment.appointment_id,
      prescription_id: ''
    };

    // TODO: potential refactor to move each step into separate function
    if (!appointment.audit_appt_id) {
      // Step 1 - Prepare Documents
      updateProgressModal({ ProgressModal, percentage: 1, message: 'Preparing Documents' });
      const { failedToSend } = await prepareDocumentsOnCompleteAppointment();
      if (failedToSend) {
        setAlert({
          type: 'error',
          message: 'Failed to send Documents'
        });
        updateProgressModal({
          ProgressModal,
          percentage: 100,
          status: 'exception',
          message: 'Failed to send Documents'
        });

        return;
      }

      // Step 2 - Medications
      // it is possible to get to here without loading up the medications that were initially set up and then the browser
      // refreshed. So, sadly, need to try to load them now ...
      updateProgressModal({ ProgressModal, percentage: 10, message: 'Validating Medications' });

      if (canPrescribe && prescribeMedication) {
        const { medications } = await medicationsLoader();
        setTableMedications(medications);
      }

      const incompleteMedication = getIncompleteMedication();
      if (incompleteMedication) {
        // Sets Alert within validation
        updateProgressModal({
          ProgressModal,
          percentage: 100,
          status: 'exception',
          message: `Please provide the "${incompleteMedication.duration?.trim() ? 'Frequency' : 'Duration'}" for "${incompleteMedication.vmp_name || incompleteMedication.current_vmp?.vmp_name}"`
        });
        return;
      }

      // Step 3 - DPA Check
      updateProgressModal({ ProgressModal, percentage: 20, message: 'Validating DPA' });
      if (!(await validateDPACheck())) {
        // Sets Alert within validation
        updateProgressModal({
          ProgressModal,
          percentage: 100,
          status: 'exception',
          message: 'Please ensure the DPA check is valid before completing the appointment.'
        });
        return;
      }

      if (!validateDocmanDiscussionFormField()) {
        updateProgressModal({
          ProgressModal,
          percentage: 100,
          status: 'exception',
          message: t('Please make sure you have discussed document sharing with the patient during this appointment')
        });
        return;
      }

      // Step 4 - Clinical Tab Validation
      updateProgressModal({ ProgressModal, percentage: 30, message: 'Validating Clinical Information' });

      const { isValid: clinicalTabValid, invalidMessage: clinicalTabInvalidMessage } = await validateClinicalTab();
      if (!clinicalTabValid) {
        // Sets Alert within validation
        updateProgressModal({
          ProgressModal,
          percentage: 100,
          status: 'exception',
          message: clinicalTabInvalidMessage
        });
        return;
      }

      // Step 5 - Action Tab Validation
      updateProgressModal({ ProgressModal, percentage: 40, message: 'Validating Clinical Actions' });
      const { isValid: actionTabValid, invalidMessage: actionTabInvalidMessage } = await validateActionTab();
      if (!actionTabValid) {
        setAlert({
          type: 'error',
          message: actionTabInvalidMessage
        });
        updateProgressModal({ ProgressModal, percentage: 100, status: 'exception', message: actionTabInvalidMessage });
        return;
      }

      // Step 6 - Appointment Outcome Validation
      updateProgressModal({ ProgressModal, percentage: 50, message: 'Validating Appointment Outcome' });
      if (!appointment.appointment_outcome_code) {
        setAlert({
          type: 'error',
          message: 'Cannot complete an appointment without an outcome. Please select an outcome'
        });
        updateProgressModal({
          ProgressModal,
          percentage: 100,
          status: 'exception',
          message: 'Cannot complete an appointment without an outcome. Please select an outcome.'
        });
        return;
      }
      const outcomeFound = appointmentMeta?.appt_outcomes?.find(
        (outcome) => outcome?.appt_outcome_code === appointment.appointment_outcome_code
      );
      if (!outcomeFound) {
        setAlert({
          type: 'error',
          message: 'The appointment outcome could not be found.'
        });
        updateProgressModal({
          ProgressModal,
          percentage: 100,
          status: 'exception',
          message: 'The appointment outcome could not be found.'
        });
        return;
      }
      // This should only trigger for v1 outcomes, as appt_auth_f2f_flag isn't used for v2
      if ('appt_auth_f2f_flag' in outcomeFound && outcomeFound.appt_auth_f2f_flag) {
        if ((appointment.auth_granted || outcomeFound.appt_auth_required_flag) && !f2fReferralPosted) {
          // if self book auth granted, check for f2f post referral
          setAlert({
            type: 'error',
            message: 'Please complete and save the "Face to face referral" fields in the "Outcome" tab'
          });
          updateProgressModal({
            ProgressModal,
            percentage: 100,
            status: 'exception',
            message: 'Please complete and save the "Face to face referral" fields in the "Outcome" tab'
          });

          return;
        }
      }

      // Step 7 - Save PDFs
      updateProgressModal({ ProgressModal, percentage: 60, message: 'Saving Files' });
      await pdfSave(false);

      // Step 8 - Validate Prescriptions & Prescribe Medications
      updateProgressModal({ ProgressModal, percentage: 70, message: 'Prescribing Medications' });
      if (!investigationRequests) {
        setAlert({
          type: 'error',
          message: 'Unable to load investigation requests'
        });
        updateProgressModal({
          ProgressModal,
          percentage: 100,
          status: 'exception',
          message: 'Unable to load investigation requests'
        });
        return;
      }

      const isCaseRevAmend = appointment?.appt_type_code === CaseReviewApptTypes.CASEREVADD;
      const hasMedicationsToPrescribe = tableMedications && canPrescribe && tableMedications?.length > 0;
      if (isCaseRevAmend || hasMedicationsToPrescribe) {
        const signaturePharmacyAction = isCaseRevAmend
          ? actions.amendSignaturePharmacy
          : actions.prescribeSignaturePharmacy;

        const { prescription_id, status } = await dispatch(signaturePharmacyAction(appointment.appointment_id));
        option.prescription_id = prescription_id;

        const genericErrorMsg =
          'Sorry, unable to complete your appointment because of problems prescribing ' +
          'your medication. Either contact your administrator or remove the prescription and try again.';

        const controlledDrugErrorMsg =
          'You are attempting to prescribe a controlled drug, which can only be ' +
          'done by a GP. Either change the medication, or discharge and refer the patient to a GP.';

        // 400 response returned when attempting to prescribe controlled drugs
        // The API response does return a cause, but we can't seem to add it to the Response without upsetting
        // other logic depending on the fetchData function
        const message = status === 400 ? controlledDrugErrorMsg : genericErrorMsg;

        if (!prescription_id && tableMedications?.length && prescribeMedication) {
          setAlert({
            type: 'error',
            message
          });
          updateProgressModal({
            ProgressModal,
            percentage: 100,
            status: 'exception',
            message
          });
          return;
        }
      }
    }

    // Step 9 - Complete Appointment
    const response = await dispatch(actions.completeSession({ ...option }), false);
    if (response.type === actionTypes.ACTION_FAIL && response?.error?.code !== 412) {
      let errorHandled = false;

      // Handle 409 errors where response data is in payload instead of error
      if (response?.payload?.status === 409) {
        response.payload.json?.().then((err: { message: string }) => {
          const errorMessage = err?.message || `Unable to complete appointment. Status: ${response.payload.status}`;
          setAlert({
            type: 'error',
            message: errorMessage
          });
          updateProgressModal({
            ProgressModal,
            percentage: 100,
            status: 'exception',
            message: errorMessage
          });
          errorHandled = true;
        });
        if (errorHandled) return;
      }

      response?.error?.json?.().then((err: { message: string }) => {
        setAlert({
          type: 'error',
          message: err?.message || `Unable to complete appointment. Status: ${response.status}`
        });
        updateProgressModal({
          ProgressModal,
          percentage: 100,
          status: 'exception',
          message: err?.message || `Unable to complete appointment. Status: ${response.status}`
        });
        errorHandled = true;
      });
      if (errorHandled) return;
    }

    if (response?.appt_id) {
      // Could reload? But not sure if necessary
      cleanUpAppointmentStorage();
    } else {
      type ResponseStatus = 405 | 406 | 412;

      // Define the response map with the specific type
      const responseMap: Record<ResponseStatus, string> = {
        405: 'Cannot complete an appointment in the future',
        406: 'Member cannot be granted F2F booking auth',
        412: 'You cannot complete an appointment before it takes place'
      } as const;

      const errorCode = response?.error?.code || response?.status;
      const errorMessage = response?.error?.message || response?.message;

      setAlert({
        type: 'error',
        message:
          errorMessage ||
          responseMap[errorCode as ResponseStatus] ||
          'Trouble completing your appointment. Please contact your administrator'
      });

      updateProgressModal({
        ProgressModal,
        percentage: 100,
        status: 'exception',
        message:
          errorMessage ||
          responseMap[errorCode as ResponseStatus] ||
          'Trouble completing your appointment. Please contact your administrator'
      });

      return;
    }
    // await load for 2 seconds to give appointment chance to be completed on BE
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // Step 10 - Finish Off Completion Process
    updateProgressModal({
      ProgressModal,
      percentage: 100,
      status: 'success',
      message: 'Appointment Completed Successfully'
    });
    if (updatersReady && callStatus !== 'completed') {
      await endCall({ silent: true });
    }
    if (closeWS) {
      closeWS();
    }
    refetchAppointment();
    onClickHangupCall();
    queryClient.invalidateQueries({ queryKey: ['clinicianAppointment'] });
    queryClient.invalidateQueries({ queryKey: ['getUserAppointment'] });
    queryClient.invalidateQueries({ queryKey: ['timeline'] });
    queryClient.invalidateQueries({ queryKey: ['userTimelineHistory'] });
    queryClient.invalidateQueries({ queryKey: ['recordingsAvailability'] });
  }, [
    loadProgressValidationModal,
    updateProgressModal,
    prepareDocumentsOnCompleteAppointment,
    canPrescribe,
    prescribeMedication,
    getIncompleteMedication,
    validateDPACheck,
    validateClinicalTab,
    validateActionTab,
    appointment.appointment_outcome_code,
    appointment.clinician_user_guid,
    appointmentId,
    appointment.auth_granted,
    appointmentMeta?.appt_outcomes,
    pdfSave,
    loadInvestigationRequests,
    tableMedications,
    dispatch,
    queryClient,
    endCall,
    callStatus,
    updatersReady,
    refetchAppointment,
    setAlert,
    medicationsLoader,
    setTableMedications,
    f2fReferralPosted,
    cleanUpAppointmentStorage,
    closeWS
  ]);

  const completeAppointmentConfirmation = useCallback(async () => {
    Modal.destroyAll(); // Clear any open modals before syncing to complete the appointment
    Modal.confirm({
      title: t('Complete Appointment Confirmation'),
      content: 'Are you sure you want to complete this appointment?',
      modalRender: (originalNode) => <div data-testid="complete-appointment-confirmation-modal">{originalNode}</div>,
      onOk: completeAppointment
    });
  }, [completeAppointment]);

  const completedPDFRequest = useCallback(
    async (ireq_id: number) => {
      const loaded = await loadPDFDocument(ireq_id);

      if (loaded.error) {
        setAlert({
          type: 'error',
          message: 'There was an error loading the investigation request.'
        });
        Modal.error({
          title: t('Error Loading Investigation Request'),
          content: 'There was an error loading the investigation request.',
          modalRender: (originalNode) => (
            <div data-testid="error-loading-investigation-request-modal">{originalNode}</div>
          ),
          width: 'auto',
          centered: true
        });
        return false;
      }

      const { meta, document } = loaded;

      if (!meta.edited_flag) {
        setAlert({
          type: 'error',
          message: 'One or more requests have not been saved. Please fill in all required fields and save the changes.'
        });
        Modal.error({
          title: t('Requests have not been completed'),
          content:
            'One or more requests have not been saved. Please fill in all required fields and save the changes, or delete the action.',
          modalRender: (originalNode) => <div data-testid="requests-have-not-been-completed-modal">{originalNode}</div>,
          width: 'auto',
          centered: true
        });

        return false;
      }

      const missingRequiredFields = getMissingFields(document);

      if (missingRequiredFields.length > 0) {
        setAlert({
          type: 'error',
          message: `An action has required fields that are empty. Please complete them or delete the action. Fields ${missingRequiredFields.join(', ')}`
        });
        Modal.error({
          title: t('Actions have not been completed'),
          content: (
            <p>
              {t(
                'One or more requests have not been saved. Please fill in all required fields and save the change, or delete the action.'
              )}
            </p>
          ),
          modalRender: (originalNode) => <div data-testid="actions-have-not-been-completed-modal">{originalNode}</div>,
          width: 'auto',
          centered: true
        });

        return false;
      }

      return document;
    },
    [getMissingFields, loadPDFDocument, setAlert]
  );

  /** validates all IR's, flattens PDF IR's as required */
  const investigationRequestsReadyToSave = useCallback(
    async (investigationRequests: InvestigationRequest[]) => {
      for (const surveyIR of investigationRequests.filter(
        (ir) => typeof ir.surveyjs_id === 'number' && ir.surveyjs_id
      )) {
        if (surveyIR.surveyjs_id) {
          const investigationRequestSurveyResponse = await dispatch(actions.getSurveyJsId(surveyIR.surveyjs_id));
          const surveyIsComplete = investigationRequestSurveyResponse.user_surveyjs[0].completed_flag;
          if (!surveyIsComplete) {
            setAlert({
              type: 'error',
              message:
                'An investigation request has required fields that are empty. Please complete them or delete the investigation request.'
            });
            Modal.error({
              title: t('An investigation request has not been completed'),
              content:
                'An investigation request has required fields that are empty. Please complete them or delete the investigation request.',
              modalRender: (originalNode) => (
                <div data-testid="investigation-request-have-not-been-completed-modal">{originalNode}</div>
              )
            });
            return false;
          }
        }
      }

      for (const surveyIR of investigationRequests.filter((ir) => !ir.surveyjs_id)) {
        const document = await completedPDFRequest(surveyIR.ireq_id);
        if (!document) {
          return false;
        }
      }
      return true;
    },
    [completedPDFRequest, dispatch, setAlert]
  );

  const removeRequestsModal = useCallback(
    (investigationRequests: InvestigationRequest[]) => {
      Modal.confirm({
        title: t('Remove Investigation Request(s)'),
        content: 'Are you sure you want to delete all investigation requests?',
        modalRender: (originalNode) => <div data-testid="remove-investigation-request-modal">{originalNode}</div>,
        okButtonProps: { loading: deleteInvestigationRequestsLoading },
        onOk: async () => deleteInvestigationRequests(investigationRequests)
      });
    },
    [deleteInvestigationRequests, deleteInvestigationRequestsLoading]
  );

  const confirmInvestigationModal = useCallback(
    (investigationRequests: InvestigationRequest[], outcomeFound: ApptOutcomeItem) => {
      Modal.confirm({
        okButtonProps: { style: { display: 'none' } },
        cancelButtonProps: { style: { display: 'none' } },
        title: t('Unexpected Outcome for Investigation Request(s)'),
        modalRender: (originalNode) => (
          <div data-testid="unexpected-outcome-for-investigation-request-modal">{originalNode}</div>
        ),
        width: 600,
        icon: <></>,
        style: { padding: 0 },
        onCancel: () => Modal.destroyAll(),
        content: (
          <Container>
            <Divider style={{ paddingLeft: '20px', paddingRight: '20px', margin: '12px 0' }} />
            <ContentDiv>
              {t('The appointment outcome is set to ')}
              <b>{outcomeFound?.appt_outcome_desc}</b> {t(' with the following active investigation request(s)')}
              <List
                css={css`
                  max-height: 400px;
                  overflow-y: auto;
                  margin: 10px 0;
                `}
                dataSource={investigationRequests}
                renderItem={(item: InvestigationRequest) => (
                  <List.Item>
                    <ProviderCard
                      hospitalID={item?.hospital_id || 0}
                      isCompleted
                      provider={item}
                      setHospitalID={() => {}}
                      confirmInvestigationModalView
                    />
                  </List.Item>
                )}
              />
              {t('Please review and remove any requests that are no longer required, the button be')}
            </ContentDiv>
            <Divider style={{ paddingLeft: '20px', paddingRight: '20px', margin: '12px 0' }} />
            <FooterDiv>
              <Button key="cancel" onClick={Modal.destroyAll} data-testid="close-button">
                {t('Close')}
              </Button>
              <Button key="primary" type="primary" onClick={completeAppointment} data-testid="complete-appointment">
                {t('Complete appointment')}
              </Button>
              <Button
                key="remove"
                onClick={() => removeRequestsModal(investigationRequests)}
                data-testid="remove-all-investigation-requests"
              >
                {t('Remove requests')}
              </Button>
            </FooterDiv>
          </Container>
        )
      });
    },
    [completeAppointment, removeRequestsModal]
  );

  const preCompleteAppointmentValidation = useCallback(async () => {
    const displayIncompleteRequestError = () => {
      setAlert({
        type: 'error',
        message:
          'An investigation request has required fields that are empty. Please complete them or delete the investigation request.'
      });
      Modal.error({
        title: t('An investigation request has not been completed'),
        modalRender: (originalNode) => (
          <div data-testid="an-investigation-request-has-not-been-completed-modal">{originalNode}</div>
        ),
        content:
          'An investigation request has required fields that are empty. Please complete them or delete the investigation request.'
      });
    };

    if (!appointment.audit_appt_id && !letterViewed) {
      onCompleteError({
        titleMsg: 'Letter Not Viewed',
        errorMsg: 'Review the letter before completing an appointment'
      });
      return false;
    }

    if (appointment.audit_appt_id && !isAuditSurveyCompleted) {
      onCompleteError({
        titleMsg: 'Audit Survey Not Completed',
        errorMsg: 'Complete Audit Survey before completing an appointment'
      });
      return false;
    }

    const investigationRequests = await loadInvestigationRequests(displayIncompleteRequestError);
    const currentAppointmentDetailsUpdated = await getAppointmentDetails();
    // Check if there are any investigation requests
    if (investigationRequests.length === 0 && currentAppointmentDetailsUpdated.appts[0]?.invreq.length === 0)
      return true;
    // Validate InvReqs
    const outcomeFound = appointmentMeta?.appt_outcomes?.find(
      (outcome) => outcome?.appt_outcome_code === appointmentOutcome
    );
    // New Functionality which checks there is a valid outcome selected
    if (!outcomeFound || !('appt_actions' in outcomeFound)) {
      onCompleteError({
        titleMsg: 'Outcome Not Found',
        errorMsg: 'The appointment outcome could not be found.'
      });
      return false;
    }

    const v2InvReqAction =
      isAppointmentV2 && outcomeFound?.appt_actions?.find((action) => action.appt_action_code === 'INVREQ');
    const v2OutcomeAllowsInvReq = v2InvReqAction && (v2InvReqAction.enabled || v2InvReqAction.mandatory);

    // @ts-ignore
    const investigationRequestExpected = isAppointmentV1 ? outcomeFound?.appt_ireq_flag : v2OutcomeAllowsInvReq;
    const requestsReady = await investigationRequestsReadyToSave(investigationRequests);

    if (!requestsReady) return false;

    if (!investigationRequestExpected) {
      confirmInvestigationModal(investigationRequests, outcomeFound);
      return false;
    }

    return true;
  }, [
    appointmentMeta?.appt_outcomes,
    appointmentOutcome,
    confirmInvestigationModal,
    getAppointmentDetails,
    investigationRequestsReadyToSave,
    isAppointmentV1,
    isAppointmentV2,
    letterViewed,
    loadInvestigationRequests,
    onCompleteError,
    setAlert
  ]);

  const onCompleteAppointment = useCallback(async () => {
    // Check if appointment is already completed
    if (appointment.appointment_completed) {
      Modal.error({
        title: t('Appointment completed'),
        content: 'This appointment has already been completed.',
        modalRender: (originalNode) => <div data-testid="appointment-completed-modal">{originalNode}</div>,
        closable: true,
        centered: true,
        okText: 'Go to Appointments',
        onOk: () => {
          Modal.destroyAll();
          redirectAppointment();
        }
      });
      return;
    }
    // Pre Completion Validation (Letter Viewed & Investigation Requests are checked here)
    if (!(await preCompleteAppointmentValidation())) return;
    completeAppointmentConfirmation();
  }, [
    appointment.appointment_completed,
    completeAppointmentConfirmation,
    preCompleteAppointmentValidation,
    redirectAppointment
  ]);

  return {
    onCompleteAppointment
  };
};

export { useCompleteAppointment };
